import React, { useCallback } from 'react';
import { Button, ButtonProps, CircularProgress, Stack } from '@mui/material';
import { Bolt, Pause, Delete, Close } from '@mui/icons-material';
import { ReactComponent as BoltIcon } from 'new/components/icons/bolt.svg';
import { ReactComponent as PauseIcon } from 'new/components/icons/pause.svg';
import { ReactComponent as DeleteIcon } from 'new/components/icons/fi-rr-trash.svg';
import { ReactComponent as CancelIcon } from 'new/components/icons/close-cross.svg';

export type FlowActionType = 'activate' | 'pause' | 'delete' | 'cancel';

export interface FlowActionButtonProps extends Omit<ButtonProps, 'color' | 'onClick'> {
  actionType: FlowActionType;
  isLoading?: boolean;
  onClick: () => void;
  disabled?: boolean;
  showIcon?: boolean;
  label?: string;
}

const LABEL_MAP: Record<FlowActionType, string> = {
  activate: 'Activate',
  pause: 'Pause',
  delete: 'Delete',
  cancel: 'Cancel',
};

const COLOR_MAP: Record<FlowActionType, 'primary' | 'secondary' | 'error' | 'inherit' | 'info'> = {
  activate: 'primary',
  pause: 'secondary', // Uses secondary outlined style from theme
  delete: 'error',
  cancel: 'info',
};

// All buttons use outlined style, differentiated by color
const VARIANT_MAP: Record<FlowActionType, 'contained' | 'outlined'> = {
  activate: 'outlined',
  pause: 'outlined',
  delete: 'outlined',
  cancel: 'outlined',
};

function getIcon(actionType: FlowActionType): JSX.Element | null {
  switch (actionType) {
    case 'activate':
      return <BoltIcon width={14} height={14} />;
    case 'pause':
      return <PauseIcon width={14} height={14} />;
    case 'delete':
      return <DeleteIcon width={14} height={14} />;
    case 'cancel':
      return <CancelIcon width={14} height={14} />;
    default:
      return null;
  }
}

function getFallbackIcon(actionType: FlowActionType): JSX.Element | null {
  switch (actionType) {
    case 'activate':
      return <Bolt />;
    case 'pause':
      return <Pause />;
    case 'delete':
      return <Delete />;
    case 'cancel':
      return <Close />;
    default:
      return null;
  }
}

const defaultStyleSx = {
  fontSize: '14px',
  textTransform: 'none',
  display: 'flex',
  gap: '4px',
  alignItems: 'center',
  justifyContent: 'center',
};

export function FlowActionButton({
  actionType,
  isLoading = false,
  onClick,
  disabled = false,
  variant: variantOverride,
  showIcon = true,
  label: labelOverride,
}: FlowActionButtonProps) {
  const isDisabled = disabled;
  const color = COLOR_MAP[actionType];
  const variant = variantOverride || VARIANT_MAP[actionType];
  const label = labelOverride || LABEL_MAP[actionType];

  const icon = getIcon(actionType);
  const fallbackIcon = getFallbackIcon(actionType);
  const startIcon = isLoading || !showIcon ? undefined : icon || fallbackIcon;

  const safeOnClick = useCallback(() => {
    if (isLoading) return;
    onClick();
  }, [isLoading, onClick]);

  return (
    <Button
      variant={variant}
      color={color}
      onClick={safeOnClick}
      disabled={isDisabled}
      startIcon={startIcon}
      sx={defaultStyleSx}
    >
      {isLoading && (
        <Stack marginRight="4px" direction="row" alignItems="center" justifyContent="center">
          <CircularProgress size={14} color="inherit" />
        </Stack>
      )}
      {label}
    </Button>
  );
}
